// Making a token set's values. Every token in a set holds a plain value: a colour, a length, a number, a shadow or a
// gradient written out in full. Some tokens have a formula in tokens.ts (`derive`) that makes their value from the set's
// others, the large corner from the roundness, the page from the surface; only making a set uses them (the generator, and
// filling in a set's values once). The app never works anything out from a set as it runs.
import { SET_TOKENS, deriveCss, deriveSources, tokenSpec } from './tokens';
import { readColour, themeValues, tokenColour, tokenNumber, writeColour, type TokenColour } from './colour';

const FORMULA = new Map(SET_TOKENS.flatMap(token => (token.derive ? [[token.name, deriveCss(token.derive)] as const] : [])));

/** The tokens with a formula, in tokens.ts order. */
export const FORMULA_TOKENS: readonly string[] = [...FORMULA.keys()];

const trim = (n: number, places = 4) => String(Number(n.toFixed(places)));
const REM = 16;

// A run of arithmetic in one unit (px, %, deg or none; rem counts as px), worked out; null where it mixes units
function arithmetic(body: string): string | null {
  const text = body.replace(/(-?\d*\.?\d+)rem\b/g, (_m, n: string) => `${Number(n) * REM}px`);
  const units = new Set([...text.matchAll(/\d(px|%|deg)/g)].map(match => match[1]));
  if (units.size > 1) return null;
  const bare = text.replace(/(\d)(px|%|deg)/g, '$1');
  if (!/^[\d\s.+\-*/(),]*$/.test(bare)) return null;
  try {
    const value = Number(new Function(`return (${bare})`)());
    return Number.isFinite(value) ? `${trim(value)}${[...units][0] ?? ''}` : null;
  } catch {
    return null;
  }
}

// The arguments of a function call, split at its own commas
function argumentsOf(body: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < body.length; i++) {
    if (body[i] === '(') depth++;
    else if (body[i] === ')') depth--;
    else if (body[i] === ',' && depth === 0) {
      parts.push(body.slice(start, i).trim());
      start = i + 1;
    }
  }
  parts.push(body.slice(start).trim());
  return parts;
}

// Two colours mixed in OKLab as color-mix() does, opacity premultiplied, so a colour mixed with transparent keeps its hue
function mixInOklab(a: TokenColour, b: TokenColour, wa: number, wb: number): TokenColour | null {
  const la = srgbLab(a.rgb);
  const lb = srgbLab(b.rgb);
  const alpha = a.alpha * wa + b.alpha * wb;
  if (alpha <= 0) return { rgb: [0, 0, 0], alpha: 0 };
  const lab = la.map((v, i) => (v * a.alpha * wa + lb[i] * b.alpha * wb) / alpha) as [number, number, number];
  return { rgb: labSrgb(lab), alpha };
}

// sRGB and OKLab, as colour.ts converts them
const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const toGamma = (c: number) => (c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055);
function srgbLab([r, g, b]: [number, number, number]): [number, number, number] {
  const [lr, lg, lb] = [r, g, b].map(c => toLinear(c / 255));
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
  return [0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s, 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s, 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s];
}
function labSrgb([L, a, b]: [number, number, number]): [number, number, number] {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const linear = [4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s, -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s, -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s];
  return linear.map(c => Math.min(255, Math.max(0, toGamma(c) * 255))) as [number, number, number];
}

// A color-mix(in oklab, A [p%], B [q%]) whose colours are plain, as one colour; null where it can't be read
function colourMix(body: string): string | null {
  const [space, first, second] = argumentsOf(body);
  if (space !== 'in oklab' || !first || !second) return null;
  const part = (text: string) => {
    const m = text.match(/^(.*?)(?:\s+(-?\d*\.?\d+)%)?$/);
    return m ? { colour: readColour(m[1].trim()), share: m[2] === undefined ? null : Number(m[2]) / 100 } : null;
  };
  const a = part(first);
  const b = part(second);
  if (!a?.colour || !b?.colour) return null;
  let wa = a.share ?? (b.share === null ? 0.5 : 1 - b.share);
  let wb = b.share ?? 1 - wa;
  const sum = wa + wb;
  // Shares under 100% in all make the result as much more see-through
  const scale = sum < 1 ? sum : 1;
  wa /= sum;
  wb /= sum;
  const mixed = mixInOklab(a.colour, b.colour, wa, wb);
  return mixed && writeColour({ ...mixed, alpha: mixed.alpha * scale });
}

/** CSS with no references left worked out to plain values: every calc(), min(), max() and color-mix() in it. */
export function plainCss(css: string): string {
  let text = css;
  for (let pass = 0; pass < 500; pass++) {
    // The innermost calc(), min(), max() or bracket of arithmetic
    const arith = /(calc|min|max)?\(([^()]*)\)/g;
    let changed = false;
    text = text.replace(arith, (match, fn: string | undefined, body: string, at: number, whole: string) => {
      if (changed) return match;
      const before = whole[at - 1] ?? '';
      if (!fn && /[\w-]/.test(before)) return match;
      if (fn === 'min' || fn === 'max') {
        const values = argumentsOf(body).map(arithmetic).map(value => value?.match(/^(-?\d*\.?\d+)(px|%|deg)?$/));
        if (values.some(value => !value)) return match;
        // One unit, beside unitless numbers
        const units = new Set(values.map(value => value![2]).filter(Boolean));
        if (units.size > 1) return match;
        const numbers = values.map(value => Number(value![1]));
        changed = true;
        return `${trim(fn === 'min' ? Math.min(...numbers) : Math.max(...numbers))}${[...units][0] ?? ''}`;
      }
      const value = arithmetic(body);
      if (value === null) return match;
      changed = true;
      return value;
    });
    if (changed) continue;
    // The innermost color-mix()
    text = text.replace(/color-mix\(((?:[^()]|\([^()]*\))*)\)/, (match, body: string) => {
      if (changed || body.includes('color-mix(')) return match;
      const mixed = colourMix(body);
      if (!mixed) return match;
      changed = true;
      return mixed;
    });
    if (!changed) break;
  }
  return text.replace(/\s+/g, ' ').trim();
}

/** A token's value made by its formula, as plain data, from `valueOf` (a set's values, the ones being made holding their formulas). */
function madeValue(name: string, valueOf: (name: string) => string | undefined, made: Map<string, string>): string {
  const known = made.get(name);
  if (known !== undefined) return known;
  const spec = tokenSpec(name)!;
  const derive = spec.derive!;
  let value: string;
  if ('fixed' in derive) value = derive.fixed;
  else if (spec.kind === 'colour') {
    const colour = tokenColour(name, valueOf);
    value = colour ? writeColour(colour) : (valueOf(name) ?? '');
  } else if (spec.kind === 'gradient' || spec.kind === 'shadow') {
    // Each token it reads, as the set has it, or made first
    const filled = FORMULA.get(name)!.replace(/var\(--([\w-]+)\)/g, (_m, source: string) => {
      if (source === 'lureo-dark') return valueOf(source) ?? '0';
      return FORMULA.has(source) && valueOf(source) === FORMULA.get(source) ? madeValue(source, valueOf, made) : (valueOf(source) ?? '');
    });
    value = plainCss(filled);
  } else {
    // A length made from one in rem is in rem too, so it scales with the page's text as its source does; a full pill or
    // more is written as a full pill, 9999px
    const n = tokenNumber(name, valueOf);
    const inRem = deriveSources(derive).some(source => /rem\s*$/.test(valueOf(source) ?? ''));
    value = !Number.isFinite(n)
      ? (valueOf(name) ?? '')
      : spec.kind !== 'length'
        ? trim(n, 3)
        : n >= 9999
          ? '9999px'
          : inRem
            ? `${trim(n / REM, 4)}rem`
            : `${trim(n, 3)}px`;
  }
  made.set(name, value);
  return value;
}

/**
 * A set's tokens with `names` (tokens with a formula) made by their formulas from its other values, as plain data. Each is
 * made from the set's values as they are, and from the others in `names` as made now.
 */
export function madeByFormula(tokens: Record<string, string>, mode: 'light' | 'dark', names: readonly string[]): Record<string, string> {
  const remade = names.filter(name => FORMULA.has(name));
  const view = { ...tokens, ...Object.fromEntries(remade.map(name => [name, FORMULA.get(name)!])) };
  const valueOf = themeValues(view, mode);
  const made = new Map<string, string>();
  return { ...tokens, ...Object.fromEntries(remade.map(name => [name, madeValue(name, valueOf, made)])) };
}

/** The tokens with a formula that read any of `names`, directly or through another. */
export function readersOf(names: Iterable<string>): Set<string> {
  const changed = new Set(names);
  const readers = new Set<string>();
  for (let grew = true; grew; ) {
    grew = false;
    for (const name of FORMULA_TOKENS) {
      if (readers.has(name)) continue;
      if (deriveSources(tokenSpec(name)!.derive!).some(source => changed.has(source) || readers.has(source))) {
        readers.add(name);
        grew = true;
      }
    }
  }
  return readers;
}

/** Whether a value is plain data: no reference to another token, and no arithmetic left in it. */
export const isPlain = (value: string): boolean => !/var\(|calc\(|color-mix\(|min\(|max\(/.test(value);
